import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Checkbox, Divider } from 'ui';
import { Checkbox as AntdCheckbox, Form, FormInstance, Spin } from 'antd';
import { useIntl } from 'react-intl';
import useQueryApiClient from 'utils/useQueryApiClient';

import {
  Grouped,
  PrefMap,
  NotificationPreferencesProps,
  PreferencesResponse,
} from './types';
import { StyledNotificationPreferences } from './style';

type EnumItem = { id: string; value: string };

interface NotificationPreferencesComponentProps extends NotificationPreferencesProps {
  form: FormInstance<PrefMap>;
  onSavingChange?: (saving: boolean) => void;
}

function NotificationPreferences({
  form,
  onSaved,
  onDirtyChange,
  onSavingChange,
}: NotificationPreferencesComponentProps) {
  const intl = useIntl();

  const [types, setTypes] = useState<string[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const { data: enumData, isLoading: enumLoading } = useQueryApiClient({
    request: { url: 'api/v2/enums/NotificationType' },
  });

  useEffect(() => {
    if (enumData) {
      const raw = Array.isArray(enumData?.data) ? enumData.data : Array.isArray(enumData) ? enumData : [];
      const extracted = (raw as EnumItem[])
        .map((x) => (typeof x === 'string' ? x : x?.value))
        .filter((v): v is string => typeof v === 'string' && v.length > 0);
      setTypes((prev) => {
        if (prev.length === extracted.length && prev.every((v, i) => v === extracted[i])) return prev;
        return extracted;
      });

      const defaultPrefs: PrefMap = Object.fromEntries(extracted.map((k) => [k, true]));
      form.setFieldsValue(defaultPrefs);
    }
  }, [enumData, form]);

  const { refetch: fetchPrefs, isLoading: prefsLoading } = useQueryApiClient({
    request: { url: '/api/v2/notification-preferences', disableOnMount: true },
    onSuccess: (resp: PreferencesResponse) => {
      const server: PrefMap = resp?.data ?? {};
      const normalized: PrefMap = Object.fromEntries(
        types.map((k) => [k, server[k] !== undefined ? server[k] : true])
      );
      form.setFieldsValue(normalized);
      setInitialLoadComplete(true);
    },
  });

  useEffect(() => {
    if (types.length > 0) {
      fetchPrefs?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types]);

  const { appendData: putPrefs, isLoading: saving } = useQueryApiClient({
    request: { url: '/api/v2/notification-preferences', method: 'PUT' },
    onSuccess: (resp: PreferencesResponse) => {
      const server: PrefMap = resp?.data ?? {};
      const normalized: PrefMap = Object.fromEntries(
        types.map((k) => [k, server[k] !== undefined ? server[k] : true])
      );
      form.setFields(
        types.map((k) => ({ name: k, value: normalized[k], touched: false }))
      );
      onDirtyChange?.(false);
      onSaved?.(normalized);
    },
  });

  const groups = useMemo(() => {
    const grouped: Grouped = {};
    types.forEach((full) => {
      const dot = full.indexOf('.');
      const gKey = dot > -1 ? full.slice(0, dot) : full;
      if (!grouped[gKey]) grouped[gKey] = { label: '', keys: [] };
      grouped[gKey].keys.push(full);
    });
    Object.values(grouped).forEach((g) => g.keys.sort());
    return grouped;
  }, [types]);

  const handleSubmit = (values: PrefMap) => {
    const disabledOnly: Record<string, false> = {};
    for (const k of types) {
      if (!values[k]) {
        disabledOnly[k] = false;
      }
    }
    putPrefs({ preferences: disabledOnly });
  };

  const handleFieldsChange = () => {
    const isDirty = form.isFieldsTouched();
    onDirtyChange?.(isDirty);
  };

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);

  const allValues = Form.useWatch([], form);

  if (enumLoading || prefsLoading || !initialLoadComplete) {
    return (
      <StyledNotificationPreferences>
        <div className="np-loading">
          <Spin />
        </div>
      </StyledNotificationPreferences>
    );
  }

  const handleGroupToggle = (gKey: string, checked: boolean) => {
    const group = groups[gKey];
    if (!group) return;

    const update: PrefMap = {};
    group.keys.forEach((key) => {
      update[key] = checked;
    });
    form.setFieldsValue(update);
    onDirtyChange?.(true);
  };

  if (!types.length) return null;

  return (
    <StyledNotificationPreferences>
      <Form
        form={form}
        onFinish={handleSubmit}
        onFieldsChange={handleFieldsChange}
        layout="vertical"
      >
        <div className="np-groups">
          {Object.entries(groups).map(([gKey, group], idx) => {
            const groupLabel = intl.formatMessage({ id: `notifications.group.${gKey}` });
            const checkedCount = group.keys.filter((k) => allValues?.[k]).length;
            const allChecked = checkedCount === group.keys.length;
            const indeterminate = checkedCount > 0 && !allChecked;

            return (
              <div key={gKey} className="np-group">
                {!!idx && <Divider />}
                <div className="title">
                  <AntdCheckbox
                    checked={allChecked}
                    indeterminate={indeterminate}
                    onChange={(e) => handleGroupToggle(gKey, e.target.checked)}
                  />
                  <h3>{groupLabel}</h3>
                </div>
                <div className="np-grid">
                  {group.keys.map((full) => {
                    const dot = full.indexOf('.');
                    const grp = dot > -1 ? full.slice(0, dot) : full;
                    const sub = dot > -1 ? full.slice(dot + 1) : full;
                    const typeLabel = intl.formatMessage({ id: `notifications.type.${grp}.${sub}` });
                    return (
                      <Form.Item key={full} name={full} valuePropName="checked" noStyle>
                        <Checkbox label={typeLabel} />
                      </Form.Item>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Form>
    </StyledNotificationPreferences>
  );
}

NotificationPreferences.displayName = 'NotificationPreferences';
export default NotificationPreferences;
